import React from 'react';
import { Box, Flex, Heading, IconButton, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'phosphor-react';

import { GlassPageBg } from '../../../../../components/glass';
import { useContaCilEmpresaContexto } from '../../../../../hooks/useContaCilEmpresas';
import { fmtDoc } from '../conciliar/shared';
import { FadeIn } from '../plano-contas/ui';
import { AbaContas } from './components/AbaContas';
import { AbaContextoIA } from './components/AbaContextoIA';
import { AbaMemoria } from './components/AbaMemoria';
import { AbaVisaoSaude } from './components/AbaVisaoSaude';

export type AbaHub = 'visao' | 'contas' | 'contexto' | 'memoria';

const ABAS: { id: AbaHub; label: string }[] = [
  { id: 'visao', label: 'Visão & Saúde' },
  { id: 'contas', label: 'Contas bancárias' },
  { id: 'contexto', label: 'Contexto IA' },
  { id: 'memoria', label: 'Memória' },
];

function iniciais(nome?: string | null): string {
  if (!nome) return '??';
  const ps = nome.trim().split(/\s+/).filter(Boolean);
  if (ps.length === 0) return '??';
  if (ps.length === 1) return ps[0].slice(0, 2).toUpperCase();
  return (ps[0][0] + ps[ps.length - 1][0]).toUpperCase();
}

export function EmpresaHub({
  empresaId,
  aba,
  onAba,
  onVoltar,
}: {
  empresaId: number;
  aba: AbaHub;
  onAba: (a: AbaHub) => void;
  onVoltar: () => void;
}) {
  const { data: ctx } = useContaCilEmpresaContexto(empresaId);
  const nome = ctx?.razao_social || ctx?.nome || `Empresa ${empresaId}`;
  const sub = [fmtDoc(ctx?.cnpj), ctx?.regime, ctx?.external_id ? `ext${ctx.external_id}` : null]
    .filter(Boolean)
    .join(' · ');

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
        >
          <Flex align="center" gap={3} mb={5}>
            <IconButton
              aria-label="Voltar para empresas"
              icon={<ArrowLeft size={18} weight="bold" />}
              variant="ghost"
              size="sm"
              borderRadius="full"
              color="#64748B"
              _hover={{ bg: 'rgba(15, 23, 42, 0.06)', color: '#0F172A' }}
              onClick={onVoltar}
            />
            <Flex
              w="64px"
              h="64px"
              flexShrink={0}
              borderRadius="2xl"
              align="center"
              justify="center"
              bgGradient="linear(135deg, #E0F2FE 0%, #BAE6FD 100%)"
              color="#0284C7"
              fontWeight="700"
              fontSize="20px"
              boxShadow="0 8px 24px -4px rgba(14, 165, 233, 0.32)"
            >
              {iniciais(nome)}
            </Flex>
            <Box minW={0}>
              <Text
                fontSize="11px"
                color="#94A3B8"
                textTransform="uppercase"
                letterSpacing="0.1em"
                fontWeight="semibold"
              >
                Empresas
              </Text>
              <Heading
                fontSize={{ base: '20px', md: '26px' }}
                fontWeight="700"
                color="#0F172A"
                isTruncated
              >
                {nome}
              </Heading>
              {sub && (
                <Text fontSize="sm" color="#64748B" isTruncated>
                  {sub}
                </Text>
              )}
            </Box>
          </Flex>
        </motion.div>

        {/* Abas */}
        <Flex
          role="tablist"
          aria-label="Seções da empresa"
          display="inline-flex"
          gap="2px"
          p="4px"
          mb={6}
          borderRadius="full"
          bg="rgba(255, 255, 255, 0.72)"
          backdropFilter="blur(20px) saturate(180%)"
          sx={{ WebkitBackdropFilter: 'blur(20px) saturate(180%)' }}
          borderWidth="1px"
          borderColor="rgba(255, 255, 255, 0.6)"
          maxW="full"
          overflowX="auto"
        >
          {ABAS.map(a => {
            const on = aba === a.id;
            return (
              <Box
                key={a.id}
                as="button"
                role="tab"
                aria-selected={on}
                onClick={() => onAba(a.id)}
                px={4}
                py={2}
                borderRadius="full"
                fontSize="13px"
                fontWeight="600"
                whiteSpace="nowrap"
                flexShrink={0}
                color={on ? '#0EA5E9' : '#64748B'}
                bg={on ? 'white' : 'transparent'}
                boxShadow={on ? '0 2px 8px -2px rgba(15, 23, 42, 0.12)' : 'none'}
                transition="all 0.2s"
                _hover={on ? {} : { color: '#0F172A' }}
              >
                {a.label}
              </Box>
            );
          })}
        </Flex>

        <FadeIn key={aba}>
          {aba === 'visao' && <AbaVisaoSaude empresaId={empresaId} />}
          {aba === 'contas' && <AbaContas empresaId={empresaId} />}
          {aba === 'contexto' && <AbaContextoIA empresaId={empresaId} />}
          {aba === 'memoria' && <AbaMemoria empresaId={empresaId} />}
        </FadeIn>
      </Box>
    </GlassPageBg>
  );
}
